/**
 * cookieconsent-init.js — CookieConsent v3 bootstrap for dronedb.app.
 *
 * Fixed sibling of code/legal/legal-cookieconsent.php: the normative register
 * (categories, services, canonical labels, i18n, revision, endpoint) arrives
 * via window.DDBCookieConsent; this file contains only wiring, no copy text
 * (doc 02 SSoT rule: a relabel touches the PHP register only).
 *
 * Behaviour (verified against vanilla-cookieconsent 3.1.0 dist):
 *  - mode 'opt-in' (set in the register): nothing loads until consent.
 *  - Tracker scripts are printed server-side as type="text/plain"
 *    data-category=… data-service=… (the library promotes them per category).
 *  - Google Consent Mode: defaults are inline in <head> (PHP); here we only
 *    send update() maps derived from the user's choices (official guide).
 *  - Modal click contract (doc 04 §4, spec-review r4/Q3): the library's
 *    generated modal buttons are stamped with the documented
 *    data-cc="accept-all" / data-cc="accept-necessary" attributes (the library
 *    itself binds exactly those data-cc command values on elements present at
 *    run() time; generated buttons carry data-role="all|necessary" and their
 *    own click handlers — stamping never adds a second handler). The
 *    site-wide footer button lives in footer.php with
 *    data-cc="show-preferencesModal" (Art. 7(3) withdrawal).
 *  - Art. 7(1) evidence: every first consent and every save POSTs the consent
 *    object to wp-json/ddb/v1/consent (keepalive fetch, fire-and-forget).
 */
(function () {
	'use strict';

	var lib = window.CookieConsent;
	var cfg = window.DDBCookieConsent;
	if (!lib || typeof lib.run !== 'function' || !cfg || !cfg.categories) {
		return;
	}

	/* ---- helpers ---------------------------------------------------------- */

	// The register carries autoClear cookie patterns as strings; '/re/flags'
	// strings become RegExp client-side (JSON cannot carry regex literals).
	// Output shape per the v3 API: cookies: [{ name: RegExp|string, domain?, path? }].
	function rxPatterns(list) {
		var out = [];
		function toPattern(p) {
			if (typeof p === 'string' && p.charAt(0) === '/' && p.lastIndexOf('/') > 0) {
				var m = p.match(/^\/(.+)\/([gimsuy]*)$/);
				return m ? new RegExp(m[1], m[2]) : p;
			}
			return p;
		}
		(list || []).forEach(function (p) {
			if (p && typeof p === 'object') {
				var item = {};
				if (p.name !== undefined) { item.name = toPattern(p.name); }
				if (p.domain !== undefined) { item.domain = p.domain; }
				if (p.path !== undefined) { item.path = p.path; }
				out.push(item);
			} else {
				out.push({ name: toPattern(p) });
			}
		});
		return out;
	}

	// Map accepted categories -> Google Consent Mode signal map (official guide).
	// functionality/security storage are part of the necessary class: always on.
	function consentSignals(acceptedCategories) {
		function has(cat) { return (acceptedCategories || []).indexOf(cat) > -1; }
		var stats = has('statistics');
		var mkt = has('marketing');
		return {
			analytics_storage: stats ? 'granted' : 'denied',
			ad_storage: mkt ? 'granted' : 'denied',
			ad_user_data: mkt ? 'granted' : 'denied',
			ad_personalization: mkt ? 'granted' : 'denied',
			functionality_storage: 'granted',
			personalization_storage: 'denied',
			security_storage: 'granted'
		};
	}

	function updateGtagConsent() {
		if (typeof window.gtag !== 'function') {
			return;
		}
		var prefs = lib.getUserPreferences();
		window.gtag('consent', 'update', consentSignals(prefs && prefs.acceptedCategories));
	}

	// Art. 7(1) evidence: persist each consent to our own server-side log
	// client-side libraries cannot be the evidence (CookieConsent FAQ).
	var lastLogged = null;

	function logConsent(reason) {
		if (!cfg.restUrl || typeof fetch !== 'function') {
			return;
		}
		try {
			var cookie = lib.getCookie() || {};
			var prefs = lib.getUserPreferences() || {};
			var payload = {
				type: 'cookie',
				consentId: cookie.consentId || null,
				acceptType: prefs.acceptType || 'custom',
				categories: cookie.categories || [],
				services: cookie.services || {},
				revision: cookie.revision || 0,
				languageCode: cookie.languageCode || '',
				consentTimestamp: cookie.consentTimestamp || '',
				lastConsentTimestamp: cookie.lastConsentTimestamp || '',
				userAgent: (navigator && navigator.userAgent) ? String(navigator.userAgent).slice(0, 255) : '',
				page: location.href.slice(0, 191),
				reason: reason
			};
			if (!payload.consentId) {
				return;
			}
			// POST once per distinct consent state per page: the library fires
			// onFirstConsent and onConsent together on the first save.
			var key = [payload.consentId, payload.lastConsentTimestamp || payload.consentTimestamp, payload.languageCode, payload.revision, payload.acceptType].join('|');
			if (lastLogged === key) {
				return;
			}
			lastLogged = key;
			fetch(cfg.restUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
				credentials: 'same-origin',
				keepalive: true
			}).catch(function () { /* best-effort evidence, never block the UI */ });
		} catch (e) { /* never break consent for a logging hiccup */ }
	}

	// Pin the doc 04 §4 click contract onto the generated modal buttons.
	// (The real clicks are still handled by the library's own listeners; the
	// attributes only make the buttons matchable by tools/probe.js — fixing the
	// CMP configuration here is the sanctioned way, the probe selectors are
	// frozen.)
	function stampCcAttributes() {
		try {
			var map = { all: 'accept-all', necessary: 'accept-necessary' };
			var btns = document.querySelectorAll('#cc-main button[data-role]');
			for (var i = 0; i < btns.length; i++) {
				var role = btns[i].getAttribute('data-role');
				if (map[role] && !btns[i].getAttribute('data-cc')) {
					btns[i].setAttribute('data-cc', map[role]);
				}
			}
			return btns.length > 0;
		} catch (e) { /* cosmetic for automation only */ }
		return false;
	}

	// The consent modal may be injected asynchronously after run(): retry a few
	// bounded beats until the generated buttons exist (cheap; stops on success).
	function stampCcAttributesWithRetry(attempt) {
		if (stampCcAttributes() || attempt > 6) {
			return;
		}
		try {
			window.requestAnimationFrame(function () {
				setTimeout(function () { stampCcAttributesWithRetry(attempt + 1); }, 100);
			});
		} catch (e) { /* rAF unavailable: give up after the direct attempt */ }
	}

	/* ---- build library config from the register --------------------------- */

	var cats = {};
	Object.keys(cfg.categories).forEach(function (key) {
		var src = cfg.categories[key] || {};
		var cat = {};
		if (src.enabled) { cat.enabled = true; }
		if (src.readOnly) { cat.readOnly = true; }
		if (src.services) {
			var services = {};
			Object.keys(src.services).forEach(function (svc) {
				services[svc] = { label: src.services[svc] };
			});
			cat.services = services;
		}
		if (src.autoClear && src.autoClear.cookies) {
			cat.autoClear = { cookies: rxPatterns(src.autoClear.cookies) };
		}
		cats[key] = cat;
	});

	var translations = {};
	Object.keys(cfg.i18n).forEach(function (lang) {
		var t = cfg.i18n[lang];
		// Merge the per-category titles/descriptions into the library's
		// preferencesModal 'sections' shape (linkedCategory per category).
		var sections = [];
		Object.keys(cfg.categories).forEach(function (key) {
			var meta = (t.categories && t.categories[key]) || {};
			sections.push({
				title: meta.title || key,
				description: meta.description || '',
				linkedCategory: key
			});
		});
		sections.push({ description: t.moreInfo || '' });
		translations[lang] = {
			consentModal: t.consentModal,
			preferencesModal: Object.assign({}, t.preferencesModal, { sections: sections })
		};
	});

	/* ---- run -------------------------------------------------------------- */

	lib.run({
		mode: 'opt-in', // uniform worldwide standard (doc 04 §3) — set in register.
		revision: cfg.revision || 0,
		autoShow: true,
		hideFromBots: false, // the T-8 probe / CI automation must see the banner.
		autoClearCookies: true, // physically delete declined cookies (doc 04 §2 autoClear row).
		page_scripts: false, // scripts are promoted via data-category below.
		cookie: cfg.cookie || undefined,
		categories: cats,
		language: {
			default: 'en',
			autoDetect: 'browser',
			translations: translations
		},
		guiOptions: {
			consentModal: { layout: 'box', position: 'bottom center', equalWeightButtons: true, flipButtons: false },
			preferencesModal: { layout: 'box', equalWeightButtons: true, flipButtons: false }
		},
		onFirstConsent: function () {
			updateGtagConsent();
			logConsent('first');
		},
		onConsent: function () {
			updateGtagConsent();
			logConsent('save');
		},
		onChange: function () {
			updateGtagConsent();
		},
		onModalReady: function () {
			stampCcAttributesWithRetry(0);
		}
	});

	// Returning visitor: consent already stored in cc_cookie -> replay the
	// Consent Mode update immediately on every page.
	if (lib.validConsent && lib.validConsent()) {
		updateGtagConsent();
	} else {
		// Consent modal may already be open synchronously after run(): stamp too
		// (with retry, because the modal injection is not guaranteed synchronous).
		stampCcAttributesWithRetry(0);
	}
})();
